import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getTierColor, getTierName, formatNumber } from '@/lib/utils';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Cpu, Zap, Cloud } from 'lucide-react';

const tierIcons = {
  tier1_local_fast: Cpu,
  tier2_local_smart: Zap,
  tier3_cloud: Cloud,
};

export function TierDistributionChart({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <Card className="border-border bg-card h-full">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Tier Distribution</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-48">
          <p className="text-muted-foreground text-sm">No data available</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = Object.entries(data).map(([tier, info]) => ({
    name: getTierName(tier),
    value: info.count,
    percentage: info.percentage,
    color: getTierColor(tier),
    tier,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-lg">
          <p className="font-medium text-sm">{data.name}</p>
          <p className="text-xs text-muted-foreground">
            {formatNumber(data.value)} requests ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="border-border bg-card h-full" data-testid="tier-distribution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Tier Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="flex flex-col gap-2 mt-4">
          {chartData.map((item) => {
            const Icon = tierIcons[item.tier] || Cpu;
            return (
              <div key={item.tier} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: item.color }}
                  />
                  <Icon className="h-3.5 w-3.5" style={{ color: item.color }} />
                  <span className="text-xs text-muted-foreground">{item.name}</span>
                </div>
                <span className="text-xs font-mono text-foreground">{item.percentage}%</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default TierDistributionChart;
